import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  AdminAICollectionResponse,
  AdminAIPaginatedResponse,
  AICallRecordDetail,
  AICallRecordFilters,
  AICallRecordListItem,
  AIFeatureConfiguration,
  AIFeatureTestAndActivatePayload,
  AIModelCatalogCreatePayload,
  AIModelCatalogEntry,
  AIModelCatalogUpdatePayload,
  AIModelProbe,
  AIModelProbePayload,
  AIProviderOption,
} from "~/types/admin/ai";

class AdminAIModule extends HttpFactory {
  async getCallRecords(
    filters: AICallRecordFilters = {},
  ): Promise<AdminAIPaginatedResponse<AICallRecordListItem>> {
    return await this.get(ENDPOINTS.ADMIN.AI.CALL_RECORDS, filters);
  }

  async getCallRecord(callRecordId: number): Promise<AICallRecordDetail> {
    return await this.get(`${ENDPOINTS.ADMIN.AI.CALL_RECORDS}${callRecordId}/`);
  }

  async getProviders(): Promise<AdminAICollectionResponse<AIProviderOption>> {
    return await this.get(ENDPOINTS.ADMIN.AI.PROVIDERS);
  }

  async getModels(): Promise<AdminAICollectionResponse<AIModelCatalogEntry>> {
    return await this.get(ENDPOINTS.ADMIN.AI.MODELS);
  }

  async createModel(
    data: AIModelCatalogCreatePayload,
  ): Promise<AIModelCatalogEntry> {
    return await this.post(ENDPOINTS.ADMIN.AI.MODELS, data);
  }

  async updateModel(
    modelId: number,
    data: AIModelCatalogUpdatePayload,
  ): Promise<AIModelCatalogEntry> {
    return await this.patch(`${ENDPOINTS.ADMIN.AI.MODELS}${modelId}/`, data);
  }

  async probeModel(
    modelId: number,
    data: AIModelProbePayload = {},
  ): Promise<AIModelProbe> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AI.MODELS}${modelId}/probe/`,
      data,
    );
  }

  async archiveModel(modelId: number): Promise<AIModelCatalogEntry> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AI.MODELS}${modelId}/archive/`,
      {},
    );
  }

  async restoreModel(modelId: number): Promise<AIModelCatalogEntry> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AI.MODELS}${modelId}/restore/`,
      {},
    );
  }

  async getFeatures(): Promise<
    AdminAICollectionResponse<AIFeatureConfiguration>
  > {
    return await this.get(ENDPOINTS.ADMIN.AI.FEATURES);
  }

  async testAndActivateFeature(
    featureKey: string,
    data: AIFeatureTestAndActivatePayload,
  ): Promise<AIModelProbe> {
    return await this.post(
      `${ENDPOINTS.ADMIN.AI.FEATURES}${encodeURIComponent(featureKey)}/test-and-activate/`,
      data,
    );
  }

  async getProbe(probeId: number): Promise<AIModelProbe> {
    return await this.get(`${ENDPOINTS.ADMIN.AI.PROBES}${probeId}/`);
  }
}

export default AdminAIModule;
